/**
 * useMobileLayout - Mobile/desktop responsive state and panel visibility
 *
 * Manages:
 * - Mobile breakpoint detection (< 768px, matches Tailwind md:)
 * - Mobile stats panel visibility
 * - Chat list panel visibility
 * - Input collapse state with localStorage persistence
 *
 * @returns {Object}
 * @returns {boolean} isMobile - Whether viewport is mobile (<768px, matches Tailwind md:)
 * @returns {boolean} showMobileStats - Mobile stats panel visibility
 * @returns {boolean} showChatList - Chat list panel visibility
 * @returns {boolean} isInputCollapsed - Input area collapsed state
 * @returns {Function} toggleMobileStats - Toggle mobile stats panel
 * @returns {Function} toggleChatList - Toggle chat list panel
 * @returns {Function} toggleInputCollapse - Toggle input collapse
 * @returns {Function} closeMobileStats - Close mobile stats panel
 * @returns {Function} closeChatList - Close chat list panel
 */

import { useState, useEffect, useCallback } from 'react';

const MOBILE_BREAKPOINT = 768; // Match Tailwind's md: breakpoint for consistency
const STORAGE_KEY = 'chatInputCollapsed';

export const useMobileLayout = () => {
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < MOBILE_BREAKPOINT);

  // Panel visibility
  const [showMobileStats, setShowMobileStats] = useState(false);
  const [showChatList, setShowChatList] = useState(false);

  // Input collapse state with localStorage
  const [isInputCollapsed, setIsInputCollapsed] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved === 'true';
  });

  // Window resize handler
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle mobile stats panel
  const toggleMobileStats = useCallback(() => {
    setShowMobileStats(prev => !prev);
  }, []);

  // Toggle chat list panel
  const toggleChatList = useCallback(() => {
    setShowChatList(prev => !prev);
  }, []);

  // Toggle input collapse with localStorage persistence
  const toggleInputCollapse = useCallback(() => {
    setIsInputCollapsed(prev => {
      const newState = !prev;
      localStorage.setItem(STORAGE_KEY, newState.toString());
      return newState;
    });
  }, []);

  // Close mobile stats panel
  const closeMobileStats = useCallback(() => {
    setShowMobileStats(false);
  }, []);

  // Close chat list panel
  const closeChatList = useCallback(() => {
    setShowChatList(false);
  }, []);

  return {
    isMobile,
    showMobileStats,
    showChatList,
    isInputCollapsed,
    toggleMobileStats,
    toggleChatList,
    toggleInputCollapse,
    closeMobileStats,
    closeChatList
  };
};
